import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { tw } from '@/lib/tailwind';
import { useNavigate } from '@tanstack/react-router';
import UserMenuDialogItem from '../UserMenuDialogItem/UserMenuDialogItem';
import { getUserMenuDialogConfig } from './UserMenuDialogConfig';
import { getAvailableRegions } from '@/constants/regions';
import { hasDatacenterAccess } from '@/utils/datacenter';
import { useUserDatacenters } from '@/hooks/useUserDatacenters';
import { useRegionChange } from '@/hooks/useRegionChange';
import { useImpersonatorStatusQuery } from '@/api/queries';
import ImpersonateUserDialog from '../ImpersonateUserDialog/ImpersonateUserDialog';
import { useLogout } from '@/hooks/useLogout';

import { useBoundStore } from '@/store';
import type { Region } from '@/store/slices/createThoughtspotSlice';

export enum UserMenuDialogType {
  UserAvatar = 'userAvatar',
  UserRegion = 'userRegion',
}

interface UserMenuDialogProps {
  isOpen: boolean;
  onClose: () => void;
  htmlRef?: React.RefObject<HTMLDialogElement | null>;
  type: UserMenuDialogType;
}

export default function UserMenuDialog({ htmlRef, isOpen, onClose, type }: UserMenuDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { userDatacenters, isLoading: isLoadingDatacenters, error: datacentersError } = useUserDatacenters();
  const handleLogout = useLogout();
  const handleRegionChange = useRegionChange();

  const [isImpersonateDialogOpen, setIsImpersonateDialogOpen] = useState(false);

  const { data: impersonatorData } = useImpersonatorStatusQuery({
    enabled: type === UserMenuDialogType.UserAvatar,
  });
  const isImpersonator = impersonatorData?.impersonator ?? false;

  const allRegions = getAvailableRegions() as Region[];
  const regions = allRegions.filter((region) => hasDatacenterAccess(region, userDatacenters));

  const selectedRegion = useBoundStore((state) => state.selectedRegion);

  const handleSupportAccess = () => {
    onClose();
    navigate({ to: '/dashboard/support-access' });
  };

  const handleImpersonateUser = () => {
    onClose();
    setIsImpersonateDialogOpen(true);
  };

  const handleUserPreference = () => {
    onClose();
    navigate({ to: '/dashboard/user-preference' });
  };

  const userMenuDialogData =
    type === UserMenuDialogType.UserAvatar
      ? getUserMenuDialogConfig.userAvatar({
          user,
          onLogout: handleLogout,
          onSupportAccess: handleSupportAccess,
          onImpersonateUser: handleImpersonateUser,
          onUserPreference: handleUserPreference,
          isImpersonator,
        })
      : getUserMenuDialogConfig.userRegion({ regions, onChangeRegion: handleRegionChange, selectedRegion });

  return (
    <>
      <dialog
        id="user-menu-dialog"
        aria-label="User account menu"
        role="menu"
        open={isOpen}
        ref={htmlRef}
        className={tw(
          `absolute top-10 right-0 left-auto z-30`,
          `w-90 bg-white p-1`,
          `border-border shadow-table flex flex-col rounded-md border border-solid`,
          isOpen && 'visible opacity-100',
          !isOpen && 'invisible opacity-0',
        )}
      >
        {type === UserMenuDialogType.UserRegion && (
          <>
            {isLoadingDatacenters && (
              <div
                className="px-4 py-3 text-sm text-gray-600"
                role="status"
                aria-live="polite"
              >
                Loading available regions...
              </div>
            )}
            {datacentersError && (
              <div
                className="px-4 py-3 text-sm text-red-600"
                role="alert"
              >
                Failed to load regions. Please try again later.
              </div>
            )}
            {!isLoadingDatacenters && !datacentersError && regions.length === 0 && (
              <div
                className="px-4 py-3 text-sm text-gray-600"
                role="status"
              >
                No regions available for your account.
              </div>
            )}
          </>
        )}
        <nav>
          <ul
            role="menu"
            className="flex flex-col gap-1"
          >
            {userMenuDialogData.map((item) => (
              <UserMenuDialogItem
                key={item.label}
                {...item}
              />
            ))}
          </ul>
        </nav>
      </dialog>

      <ImpersonateUserDialog
        isOpen={isImpersonateDialogOpen}
        onClose={() => setIsImpersonateDialogOpen(false)}
      />
    </>
  );
}
